import moment from "moment";
import momentTimezone from "moment-timezone";

const presets = {
    today: [moment().startOf('day'), moment().endOf('day')],
    yesterday: [moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day')],
    thisWeek: [moment().startOf('week'), moment().endOf('week')],
    lastWeek: [moment().subtract(1, 'week').startOf('week'), moment().subtract(1, 'week').endOf('week')],
    thisMonth: [moment().startOf('month'), moment().endOf('month')],
    lastMonth: [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
};

const periodPresets = {
    today: { key: 'today', label: 'Hoje', value: presets.today, previous: presets.yesterday, previousLabel: 'Ontem' },
    yesterday: { key: 'yesterday', label: 'Ontem', value: presets.yesterday },
    thisWeek: { key: 'thisWeek', label: 'Essa Semana', value: presets.thisWeek, previous: presets.lastWeek, previousLabel: 'Semana Passada' },
    thisMonth: { key: 'thisMonth', label: 'Esse Mês', value: presets.thisMonth, previous: presets.lastMonth, previousLabel: 'Mês Passado' },
    lastMonth: { key: 'lastMonth', label: 'Mês Passado', value: presets.lastMonth },
};

const periodPresetsArray = Object.values(periodPresets);

const formatDateTime = (datetime, format = 'DD/MM/YYYY HH:mm', useLocalTime = false) =>
    moment(datetime).utc(useLocalTime).format(format)

const formatDateTimeBRT = (datetime, format = 'DD/MM/YYYY HH:mm') =>
    datetime ? momentTimezone.utc(datetime).tz('America/Sao_Paulo').format(format) : ''

const getTimeDifference = (unit, datetime1, datetime2) =>
    moment(datetime2).utc().diff(moment(datetime1).utc(), unit);

const getDate = (date) => moment(date);

const momentInstance = () => moment;

const isSameDate = (initDate, finalDate, unit) => {

    if (!initDate || !finalDate)
        return false;

    return moment(moment(initDate).format('YYYY-MM-DD')).isSame(moment(finalDate).format('YYYY-MM-DD'), unit);
}

const getTimeDifferenceUntilDate = (initialDate, finalDate, defaultText = "Solicitação realizada") => {

    const initial = moment(initialDate);
    const final = moment(finalDate);

    const diff = final.diff(initial);

    if (diff < 0) {
        return defaultText;
    }

    const duration = moment.duration(diff);

    let timeKey = 'belowAHour';
    timeKey = duration.asHours() >= 1 ? 'overAHour' : timeKey;
    timeKey = duration.asDays() >= 1 ? 'overADay' : timeKey;

    const timeTexts = {
        overADay: time => "Em " + Math.floor(time.asDays()) + " dias",
        overAHour: time => "Em " + Math.floor(time.asHours()) + " horas e " + time.minutes() + " minutos",
        belowAHour: time => "Em " + time.minutes() + " minutos",
    };

    return timeTexts[timeKey](duration);
}

const getBusinessDatesCount = (date, dateStart = null) => {
    let count = 0;
    const initialDate = dateStart ? new Date(dateStart) : new Date();

    initialDate.setHours(0, 0, 0, 0);
    const endDate = new Date(date);
    endDate.setHours(0, 0, 0, 0);

    while (initialDate < endDate) {
        const dayOfWeek = initialDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) count++;
        initialDate.setDate(initialDate.getDate() + 1);
    }
    return count;
};

const addBusinessDays = (days) => {
    const date = new Date();
    let addedDays = 0;

    while (addedDays < days) {
        date.setDate(date.getDate() + 1);
        const dayOfWeek = date.getDay();

        if (dayOfWeek !== 0 && dayOfWeek !== 6)
            addedDays++;
    }

    return date;
};

const getTomorrowDate = () => {

    const today = new Date();
    const tomorrow = new Date(today);

    tomorrow.setDate(today.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
};

const formatSecondsAsClock = (totalSeconds) => {

    if (!totalSeconds)
        return null;

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const formatDurationFromMinutes = (durationInMinutes = 0, minUnit = 'minutes') => {

    const formattedDurationParts = [];
    const dateFormatSteps = getDateFormatSteps(minUnit);
    let currentDuration = durationInMinutes;

    for (const step of dateFormatSteps) {

        const rawValue = currentDuration / step.denominatorToUnitValue;
        const value = step.unit === minUnit ? Math.ceil(rawValue) : Math.floor(rawValue);

        if (value <= 0)
            continue;

        currentDuration = currentDuration % step.denominatorToUnitValue;
        formattedDurationParts.push(step.getLabel(value));
    }

    return formattedDurationParts.reduce((acc, curr, index) => {

        if (formattedDurationParts.length === 1)
            return curr;

        if (index === formattedDurationParts.length - 1)
            return `${acc} e ${curr}`;

        return `${acc}${acc ? ', ' : ''}${curr}`;

    }, '');
};

const getDateFormatSteps = (minUnit) => {

    const minUnitStepIndex = dateFormatSteps.findIndex(step => step.unit === minUnit);
    if (minUnitStepIndex === -1)
        return dateFormatSteps;

    return dateFormatSteps.slice(0, minUnitStepIndex + 1);
}

const dateFormatSteps = [
    {
        unit: 'years',
        denominatorToUnitValue: 525600,
        getLabel: (years) => `${years} ano${years > 1 ? 's' : ''}`,
    },
    {
        unit: 'months',
        denominatorToUnitValue: 43200,
        getLabel: (months) => `${months} ${months > 1 ? 'meses' : 'mês'}`,
    },
    {
        unit: 'days',
        denominatorToUnitValue: 1440,
        getLabel: (days) => `${days} dia${days > 1 ? 's' : ''}`,
    },
    {
        unit: 'hours',
        denominatorToUnitValue: 60,
        getLabel: (hours) => `${hours} hora${hours > 1 ? 's' : ''}`,
    },
    {
        unit: 'minutes',
        denominatorToUnitValue: 1,
        getLabel: (minutes) => `${minutes} minuto${minutes > 1 ? 's' : ''}`
    }
];

const formatDateTimeInTimezone = (value, timezone) =>
    momentTimezone.utc(value).tz(timezone).format('YYYY-MM-DDTHH:mm:ss.SSS')

const subtract = (date, value, unit = 'hour') =>
    moment(date).subtract(value, unit);

const formatRelativeTime = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days < 7) return "há poucos dias";
    if (days < 30) return `há ${Math.floor(days / 7)} semana${Math.floor(days / 7) > 1 ? "s" : ""}`;
    const months = Math.floor(days / 30);
    if (months < 12) return `há ${months} ${months > 1 ? "meses" : "mês"}`;
    return `há mais de 1 ano`;
};

const getDaysSince = (date) => moment().startOf('day').diff(moment(date).startOf('day'), 'days');

const formatElapsedDays = (date) => {

    if (!date)
        return '';

    const days = getDaysSince(date);

    if (days <= 0)
        return 'hoje';

    return days === 1 ? 'há 1 dia' : `há ${days} dias`;
};

const formatDaysUntil = (date) => {

    if (!date)
        return '';

    const days = -getDaysSince(date);

    if (days < 0)
        return formatElapsedDays(date);

    if (days === 0)
        return 'hoje';

    return days === 1 ? 'amanhã' : `em ${days} dias`;
};

const formatShortRelativeTime = (dateStr) => {

    if (!dateStr)
        return '';

    const elapsedSeconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);

    if (!Number.isFinite(elapsedSeconds) || elapsedSeconds < 45)
        return 'agora';

    const minutes = Math.round(elapsedSeconds / 60);
    if (minutes < 60)
        return `há ${minutes} min`;

    const hours = Math.round(minutes / 60);
    if (hours < 24)
        return `há ${hours} h`;

    return `há ${Math.round(hours / 24)} d`;
};

export default {
    formatDurationFromMinutes,
    formatSecondsAsClock,
    formatDateTime,
    formatDateTimeBRT,
    getTimeDifference,
    isSameDate,
    getTimeDifferenceUntilDate,
    getDate,
    getBusinessDatesCount,
    momentInstance,
    addBusinessDays,
    getTomorrowDate,
    formatDateTimeInTimezone,
    periodPresets,
    periodPresetsArray,
    subtract,
    formatRelativeTime,
    formatShortRelativeTime,
    formatElapsedDays,
    formatDaysUntil,
};